import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { groupWeeksByPeriod } from '../../../../components/period-summary/period-summary.helpers';
import TrainingLogPageHeader from '../../components/training-log-page-header/training-log-page-header.component';
import '../../../../components/period-summary/period-summary.component.css';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import { useTrainingLogContext } from '../../training-log.context';
import LogPeriodSummary from './components/log-period-summary.component';
import LogPeriodTabs from './components/log-period-tabs.component';
import LogWeekCard from './components/log-week-card.component';
import { getDefaultPeriodIndex } from './log.helpers';
import './log.page.css';

const LogPage: FC = () => {
  const { seasonStart, stats, loading, error, attendanceTeams, attendanceLoading, hideAttendance } =
    useTrainingLogContext();
  const { showSnackbar } = useSnackbarHelper();
  const [searchParams] = useSearchParams();

  const periods = stats?.periodBreakdown ?? [];
  const weeks = stats?.weekBreakdown ?? [];

  const periodFromUrl = useMemo(() => {
    const param = searchParams.get('period');
    if (!param || periods.length === 0) {
      return null;
    }
    const parsed = parseInt(param, 10);
    return periods.some((p) => p.index === parsed) ? parsed : null;
  }, [searchParams, periods]);

  const defaultPeriodIndex = useMemo(() => getDefaultPeriodIndex(periods, weeks), [periods, weeks]);

  const [userSelectedPeriodIndex, setUserSelectedPeriodIndex] = useState<number | null>(null);

  const selectedPeriodIndex = userSelectedPeriodIndex ?? periodFromUrl ?? defaultPeriodIndex;

  const scrolledToRef = useRef<string | null>(null);

  useEffect(() => {
    if (error) {
      showSnackbar('There was an error loading your training stats. Please try again later.', 'error');
    }
  }, [error, showSnackbar]);

  useEffect(() => {
    setUserSelectedPeriodIndex(null);
  }, [seasonStart]);

  useEffect(() => {
    setUserSelectedPeriodIndex(null);
    scrolledToRef.current = null;
  }, [periodFromUrl]);

  const selectedPeriod = useMemo(
    () => periods.find((p) => p.index === selectedPeriodIndex) ?? periods[0],
    [periods, selectedPeriodIndex]
  );

  const periodWeeks = useMemo(
    () => (selectedPeriod ? groupWeeksByPeriod(weeks, selectedPeriod.index) : []),
    [weeks, selectedPeriod]
  );

  const weekParam = searchParams.get('week');
  const highlightWeekIndex = weekParam ? parseInt(weekParam, 10) : null;

  useEffect(() => {
    scrolledToRef.current = null;
  }, [highlightWeekIndex]);

  useEffect(() => {
    if (!highlightWeekIndex || loading) {
      return;
    }

    const periodParam = searchParams.get('period');
    if (periodParam && parseInt(periodParam, 10) !== selectedPeriodIndex) {
      return;
    }

    const weekInView = periodWeeks.some((w) => w.index === highlightWeekIndex);
    if (!weekInView) {
      return;
    }

    const scrollKey = `${selectedPeriodIndex}-${highlightWeekIndex}`;
    if (scrolledToRef.current === scrollKey) {
      return;
    }

    const el = document.getElementById(`log-week-${highlightWeekIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrolledToRef.current = scrollKey;
    }
  }, [highlightWeekIndex, loading, selectedPeriodIndex, periodWeeks, searchParams]);

  const handleSelectPeriod = (index: number) => {
    setUserSelectedPeriodIndex(index);
  };

  return (
    <div className="log-page">
      <TrainingLogPageHeader
        title="Training Log"
        subtitle={
          <>
            Detailed weekly training entries
            {selectedPeriod ? ` — Period ${selectedPeriod.index}` : ''}
          </>
        }
        showSeasonDateRange
      />

      {loading ? (
        <div className="log-loading">
          <div className="log-loading-tabs" />
          <div className="log-loading-summary" />
          <div className="log-loading-week" />
          <div className="log-loading-week" />
        </div>
      ) : (
        <>
          {periods.length > 0 && (
            <LogPeriodTabs
              periods={periods}
              weeks={weeks}
              selectedPeriodIndex={selectedPeriodIndex}
              onSelectPeriod={handleSelectPeriod}
            />
          )}

          {selectedPeriod && (
            <LogPeriodSummary
              period={selectedPeriod}
              weeks={weeks}
              attendanceTeams={hideAttendance ? null : attendanceTeams}
              attendanceLoading={hideAttendance ? false : attendanceLoading}
            />
          )}

          <div className="log-content">
            {periodWeeks.map((week) => (
              <div
                key={week.week}
                id={highlightWeekIndex === week.index ? `log-week-${week.index}` : undefined}
                className={highlightWeekIndex === week.index ? 'log-week-highlight' : undefined}
              >
                <LogWeekCard week={week} allWeeks={weeks} />
              </div>
            ))}
            {periodWeeks.length === 0 && !loading && (
              <div className="log-period-empty">
                <div className="log-period-empty-title">No weeks in this period</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LogPage;
